import { BankAccountType } from "#graphql/supergraphEnums";
import { US_NAME } from "#utils/country_utils";
import {
  type DetailedAchPaymentPrefObjectType,
  type DetailedWirePaymentPrefObjectType,
} from "../../common/types";

// Custom comparison function for wire payment prefs. We only need to check the items that would be set in the context
// when a user creates a payment pref.
export const compareWirePaymentPrefsComparableItems = (
  previous: DetailedWirePaymentPrefObjectType,
  newPaymentPref: DetailedWirePaymentPrefObjectType,
) => {
  const nonRegionComparisons =
    previous.__typename === newPaymentPref.__typename &&
    previous.paymentMethod === newPaymentPref.paymentMethod &&
    previous?.bank?.name === newPaymentPref?.bank?.name &&
    previous?.bank?.abaNumber === newPaymentPref?.bank?.abaNumber &&
    previous?.bank?.address?.country?.id === newPaymentPref?.bank?.address?.country?.id &&
    previous?.bank?.address?.country?.name === newPaymentPref?.bank?.address?.country?.name &&
    previous?.bank?.address?.street === newPaymentPref?.bank?.address?.street &&
    previous?.bank?.address?.street2 === newPaymentPref?.bank?.address?.street2 &&
    previous?.bank?.address?.street3 === newPaymentPref?.bank?.address?.street3 &&
    previous?.bank?.address?.city === newPaymentPref?.bank?.address?.city &&
    previous?.bank?.address?.postalCode === newPaymentPref?.bank?.address?.postalCode &&
    previous?.beneficiaryName === newPaymentPref?.beneficiaryName &&
    previous.beneficiaryAccountNumber === newPaymentPref.beneficiaryAccountNumber &&
    previous?.beneficiaryAddress?.country?.id ===
      newPaymentPref?.beneficiaryAddress?.country?.id &&
    previous?.beneficiaryAddress?.country?.name ===
      newPaymentPref?.beneficiaryAddress?.country?.name &&
    previous?.beneficiaryAddress?.street === newPaymentPref?.beneficiaryAddress?.street &&
    previous?.beneficiaryAddress?.street2 === newPaymentPref?.beneficiaryAddress?.street2 &&
    previous?.beneficiaryAddress?.street3 === newPaymentPref?.beneficiaryAddress?.street3 &&
    previous?.beneficiaryAddress?.city === newPaymentPref?.beneficiaryAddress?.city &&
    previous?.beneficiaryAddress?.postalCode ===
      newPaymentPref?.beneficiaryAddress?.postalCode &&
    previous?.wireObi?.field0 === newPaymentPref?.wireObi?.field0 &&
    previous?.wireObi?.field1 === newPaymentPref?.wireObi?.field1 &&
    previous?.wireObi?.field2 === newPaymentPref?.wireObi?.field2 &&
    previous?.wireObi?.field3 === newPaymentPref?.wireObi?.field3 &&
    previous?.wireBbi?.field0 === newPaymentPref?.wireBbi?.field0 &&
    previous?.wireBbi?.field1 === newPaymentPref?.wireBbi?.field1 &&
    previous?.wireBbi?.field2 === newPaymentPref?.wireBbi?.field2 &&
    previous?.wireBbi?.field3 === newPaymentPref?.wireBbi?.field3;

  if (!nonRegionComparisons) {
    return false;
  }

  // We already know the country is the same but to check region we check if country is USA. If so, then we check state
  // If not, then we check region.
  if (newPaymentPref?.beneficiaryAddress?.country?.name === US_NAME) {
    if (
      previous?.beneficiaryAddress?.state?.code !==
      newPaymentPref?.beneficiaryAddress?.state?.code
    ) {
      return false;
    }
  } else if (
    previous?.beneficiaryAddress?.region !== newPaymentPref?.beneficiaryAddress?.region
  ) {
    return false;
  }

  // Bank address has multiple scenarios. The country is required but not the state.
  // First we check if country is US, if not, we only need to compare region.
  // If country is USA then we check if state is provided, if so, we compare state.
  // We cannot just compare state codes without first explicitly checking them because the apollo cache sets it as null
  // in the cache once it is returned from the BE. Not undefined.
  if (newPaymentPref?.bank?.address?.country?.name === US_NAME) {
    if (
      (previous?.bank?.address?.state?.code || newPaymentPref?.bank?.address?.state?.code) &&
      previous?.bank?.address?.state?.code !== newPaymentPref?.bank?.address?.state?.code
    ) {
      return false;
    }
  } else if (previous?.bank?.address?.region !== newPaymentPref?.bank?.address?.region) {
    return false;
  }

  return true;
};

export const convertWirePaymentPrefToInput = (
  paymentPrefDetail: DetailedWirePaymentPrefObjectType,
  arenaId: string,
) => {
  return {
    arenaId: arenaId,
    bank: {
      intlRoutingNumber: paymentPrefDetail.bank?.intlRoutingCode,
      name: paymentPrefDetail.bank?.name ?? "",
      routingNumber: paymentPrefDetail.bank?.abaNumber,
      swiftCode: paymentPrefDetail.bank?.swiftCode,
      address: {
        city: paymentPrefDetail?.bank?.address?.city,
        countryId: paymentPrefDetail?.bank?.address?.country?.id ?? "",
        postalCode: paymentPrefDetail?.bank?.address?.postalCode,
        region: paymentPrefDetail?.bank?.address?.region ?? null,
        stateId: paymentPrefDetail?.bank?.address?.state?.id ?? null,
        street: paymentPrefDetail?.bank?.address?.street,
        street2: paymentPrefDetail?.bank?.address?.street2,
        street3: paymentPrefDetail?.bank?.address?.street3,
      },
    },
    beneficiaryAddress: {
      city: paymentPrefDetail?.beneficiaryAddress?.city ?? "",
      countryId: paymentPrefDetail?.beneficiaryAddress?.country?.id ?? "",
      postalCode: paymentPrefDetail?.beneficiaryAddress?.postalCode ?? "",
      region: paymentPrefDetail?.beneficiaryAddress?.region ?? null,
      stateId: paymentPrefDetail?.beneficiaryAddress?.state?.id ?? null,
      street: paymentPrefDetail?.beneficiaryAddress?.street ?? "",
      street2: paymentPrefDetail?.beneficiaryAddress?.street2,
      street3: paymentPrefDetail?.beneficiaryAddress?.street3,
    },
    beneficiaryAccountNumber: paymentPrefDetail.beneficiaryAccountNumber ?? "",
    beneficiaryName: paymentPrefDetail.beneficiaryName ?? "",
    beneficiaryType: paymentPrefDetail.beneficiaryType,
    hasIntermediary: !!paymentPrefDetail.intermediaryBank,
    referenceToBeneficiary: paymentPrefDetail.referenceToBeneficiary,
    wireObi: paymentPrefDetail.wireObi
      ? {
          field0: paymentPrefDetail.wireObi?.field0,
          field1: paymentPrefDetail.wireObi?.field1,
          field2: paymentPrefDetail.wireObi?.field2,
          field3: paymentPrefDetail.wireObi?.field3,
        }
      : paymentPrefDetail.wireObi,
    wireBbi: paymentPrefDetail.wireBbi
      ? {
          field0: paymentPrefDetail.wireBbi?.field0,
          field1: paymentPrefDetail.wireBbi?.field1,
          field2: paymentPrefDetail.wireBbi?.field2,
          field3: paymentPrefDetail.wireBbi?.field3,
        }
      : paymentPrefDetail.wireBbi,
    intermediaryBank: paymentPrefDetail.intermediaryBank
      ? {
          intlRoutingNumber: paymentPrefDetail?.intermediaryBank?.intlRoutingCode,
          name: paymentPrefDetail?.intermediaryBank?.name ?? "",
          routingNumber: paymentPrefDetail?.intermediaryBank?.abaNumber,
          swiftCode: paymentPrefDetail?.intermediaryBank?.swiftCode,
          address: {
            city: paymentPrefDetail?.intermediaryBank?.address?.city,
            countryId: paymentPrefDetail?.intermediaryBank?.address?.country?.id ?? "",
            postalCode: paymentPrefDetail?.intermediaryBank?.address?.postalCode,
            region: paymentPrefDetail?.intermediaryBank?.address?.region ?? null,
            stateId: paymentPrefDetail?.intermediaryBank?.address?.state?.id ?? null,
            street: paymentPrefDetail?.intermediaryBank?.address?.street,
            street2: paymentPrefDetail?.intermediaryBank?.address?.street2,
            street3: paymentPrefDetail?.intermediaryBank?.address?.street3,
          },
        }
      : null,
  };
};

// Custom comparison function for ACH payment prefs. We only need to check the items that would be set in the context
// when a user creates a payment pref.
export const compareAchPaymentPrefsComparableItems = (
  previous: DetailedAchPaymentPrefObjectType,
  newPaymentPref: DetailedAchPaymentPrefObjectType,
): boolean => {
  return (
    previous.__typename === newPaymentPref.__typename &&
    previous.bankName === newPaymentPref.bankName &&
    previous.routingNumber === newPaymentPref.routingNumber &&
    previous.accountNumber === newPaymentPref.accountNumber &&
    previous.accountName === newPaymentPref.accountName &&
    previous.accountType === newPaymentPref.accountType &&
    previous.accountOwnership === newPaymentPref.accountOwnership &&
    previous.additionalInstructions === newPaymentPref.additionalInstructions
  );
};

export const convertAchPaymentPrefToInput = (
  paymentPrefDetail: DetailedAchPaymentPrefObjectType,
  arenaId: string,
) => {
  return {
    accountName: paymentPrefDetail.accountName ?? "",
    accountNumber: paymentPrefDetail.accountNumber ?? "",
    accountOwnership: paymentPrefDetail.accountOwnership,
    accountType: paymentPrefDetail.accountType ?? BankAccountType.Checking,
    additionalInstructions: paymentPrefDetail.additionalInstructions,
    arenaId: arenaId,
    bankName: paymentPrefDetail.bankName ?? "",
    routingNumber: paymentPrefDetail.routingNumber ?? "",
  };
};
